import { IAlbusApis } from "./IAlbusApis"
import {
	IAccessToken,
	IAppointment,
	IAppointmentRequestParams,
	IAppointmentStatusRequestParam,
	ICommAccessTokenRequestParams,
	IConsultationCountResponse,
} from "../../interfaces/api/albus/appointment"
import ApiUtils from "../ApiUtils"
import { ChronicCareSubscriptionStatus, PreferenceTypeAttributeEnum, UpdateAppointmentStatusTimeoutInSeconds } from "../../constants"
import { IDocCoachHistory } from "../../interfaces/api/albus/DoctorCoachHistory"
import { getPatientTenant } from "../../utils/helpers"
import { IAddRosterRequest, IResourceSchedule, IResourceScheduleResponse, IRosterRequest } from "../../interfaces/roster/IRoster"
import { ROSTER_UPDATE_ACTIONS } from "../../containers/Roster/helper"
import { IOrderEvent } from "../../interfaces/IOrderEvent"
import { IVitalSaveRequest } from "../../interfaces/patient/IPatientVitals"
import { IUserContactNumbers } from "../../interfaces/patient/patients"
import IRoomEventLog from "../../interfaces/IRoomEventLog"
import IDoctorImageSignedUrl from "../../interfaces/api/albus/IDoctorImageSignedUrl"
import IDocument from "../../interfaces/api/albus/IDocument"
import { PaginatedResponse } from "../../interfaces/baseInterfaces"
import { IConsultationSlotsRequest, IConsultationSlotsResponse } from "../../interfaces/api/albus/IConsulationSlots"
import { IUpdateUserPreferenceParams } from "../../interfaces/api/albus/updateUserPreference"
import { IFetchDiagnosticProviderPreferenceResponse } from "../../interfaces/api/albus/fetchDiagnosticProviderPreference"
import { IFetchSelfInstallationBookingStatusResponse } from "../../interfaces/api/albus/fetchSelfInstallationBookingStatus"
import { IFetchConciseDiagnosticOrdersPayload, IFetchConciseDiagnosticOrdersResponse } from "../../interfaces/api/albus/fetchConciseDiagnosticeOrders"
import { isNil } from "lodash"
import { ConsultationSlotsRequest, ConsultationSlotsResponse } from "../../interfaces/api/albus/ConsulationSlots"
import { IFetchDiagnosticsBookingSlotsPayload, IFetchDiagnosticsBookingSlotsResponse } from "../../interfaces/api/albus/fetchDiagnosticBookingSlots"
import { IFetchSecureAssetUploadUrlParams, IFetchSecureAssetUploadUrlResponse } from "../../interfaces/api/albus/fetchSecureAssetUploadUrl"
import { IFetchRosterLeavesParams, IFetchRosterLeavesResponse } from "../../interfaces/api/albus/fetchRosterLeaves"
import { IAssignAgentToUserParams, IAssignAgentToUserResponse } from "../../interfaces/api/albus/assignAgentToUser"
import AppointmentScheduleRequestParamsBuilder from "../../utils/modelGenerator/appointmentRequestBuilder"
import { IGetOnboardingStatusResponse } from "../../interfaces/api/albus/getOnboardingStatus"
import { ISearchDoctorsWithAvailabilityV3Payload, ISearchDoctorsWithAvailabilityV3Response } from "../../interfaces/api/albus/searchDoctorsWithAvailabilityV3"
import { IGetConsultRecapPayload, IGetConsultRecapResponse } from "../../interfaces/api/albus/getConsultRecap"
import { IBaseGetSearchPayload, IBaseGetSearchResponse } from "../../interfaces/api/common/baseGetSearch"
import { IFetchGlpMedicineOrderSummaryPayload, IUserProductOrderSummary } from "../../interfaces/api/albus/glpMedicineOrderSummary"
import { IGlpApprovalActionRequest, IPrescriptionApproval } from "../../interfaces/patient/IPrescriptionApproval"

const ALBUS_API_PREFIX = "/albus"

const CHRONIC_CARE_SUBSCRIPTION = {
	ACTIVE: ALBUS_API_PREFIX + "/chronic-care/onboarding/subscription/active?userId=$userId",
	UPCOMING: ALBUS_API_PREFIX + "/chronic-care/onboarding/subscription/upcoming?userId=$userId",
	EXPIRED: ALBUS_API_PREFIX + "/chronic-care/onboarding/subscription/expired?userId=$userId",
}

export const AlbusApiUrls = {
	appointment: "/orders/consultation/search",
	paginatedAppointment: "/orders/consultation/search/paginated",
	consultationCount: "/orders/consultation/consultation-count",
	patientDetails: "/getAllDetailsForPatient",
	userPreference: (userId: string, preferenceType: string, preferenceTypeAttribute: string) =>
		`/user-preference?userId=${userId}&preferenceType=${preferenceType}&preferenceTypeAttribute=${preferenceTypeAttribute}`,
	userDoctorHistory: (userId: string) => `/preferred-doctor/${userId}/history`,
	preferredDoctor: (userId: string, patientId: string, tenant: string) => `/preferred-doctor?userId=${userId}&patientId=${patientId}&tenant=${tenant}`,
	chronicCareSubscription: {
		[ChronicCareSubscriptionStatus.ACTIVE]: (userId: string) => `/chronic-care/onboarding/subscription/active?userId=${userId}`,
		[ChronicCareSubscriptionStatus.UPCOMING]: (userId: string) => `/chronic-care/onboarding/subscription/upcoming?userId=${userId}`,
		[ChronicCareSubscriptionStatus.EXPIRED]: (userId: string) => `/chronic-care/onboarding/subscription/expired?userId=${userId}`,
	},
	getSchedule: "/getSchedules",
	updateRoster: (action: ROSTER_UPDATE_ACTIONS): string => `/updateSchedules/?action=${action}`,
	logOrderEvent: `/order/log-events`,
	logRoomEvent: `/addTwilioRoomEventLogBulkV2`,
	savePatientVitals: (patientId: number) => `/saveVitalForPatient/${patientId}`,
	requestAudioRecording: (appointmentId: number) => `/orders/consultation/request/recording?appointmentId=${appointmentId}`,
	fetchUserContactNumber: (addCountryPrefixToNumber: boolean, userId: number) => `/userInfo?addCountryPrefixToNumber=${addCountryPrefixToNumber}&userId=${userId}`,
	getTwilioAccessTokenList: "/getTwilioAccessTokenList",
	updateStatusAppointment: (bookingId: number, appointmentId: number) => `/orders/consultation/update/status/${bookingId}/${appointmentId}`,
	doctorSignedSignatureImage: (doctorId: number) => `/doctor/${doctorId}/signature-image-download-url`,
	searchPatient: (userId: string, relationship: string) => `/patient/search?curefitUserId=${userId}&relationship=${relationship}`,
	prescriptionPdf: (consultationId: string, type: string) => `/assets/download/prescription/${consultationId}?assetType=${type}`,
	sendPrescription: (appointmentId: number, bookingId: number) => `/orders/consultation/sendPrescription/${bookingId}/${appointmentId}`,
	patientDocuments: (patientId: number) => `/assets/patient-documents/${patientId}`,
	downloadDocument: (s3Prefix: string) => `/assets/download-patient-documents/?s3Prefix=${encodeURIComponent(s3Prefix)}`,
	approvePatientDocument: (documentId: number, approved: boolean, remarks?: string) =>
		`/patient-documents/${documentId}/approve?approved=${approved}` + (remarks ? `&remarks=${encodeURIComponent(remarks)}` : ""),
	updateUserPreference: "/user-preference",
	fetchDiagnosticProviderPreference: (userId: string) => `/chronic-care/onboarding/diagnostic/provider?userId=${userId}`,
	fetchSelfInstallationBookingStatus: (userId: string) => `/chronic-care/onboarding/self-installation/status?userId=${userId}`,
	fetchConciseDiagnosticOrders: (patientId: number) => `/orders/diagnostic/concise?patientId=${patientId}`,
	fetchConsultationSlotsForDoctor: "/inventory/availability/CONSULTATION",
	fetchDiagnosticSlotsAtHome: `/inventory/availability/DIAGNOSTICS?subCategoryCode=AT_HOME_SLOT`,
	deleteDocument: (documentId: string) => `/assets/patient-documents/${documentId}`,
	uploadPatientDocV2: "/assets/upload-patient-documents/v2",
	fetchSecureAssetUrl: "/assets/report/upload/url",
	triggerVideoCallNotification: (roomId: string) => `/video-call-notification/v2?roomId=${roomId}`,
	fetchConsultationSlots: `/inventory/availability/CONSULTATION`,
	fetchRosterLeaves: ({ agentIds, endDate, startDate }: IFetchRosterLeavesParams) => `/roster/leaves?agentIds=${agentIds}&startDate=${startDate}&endDate=${endDate}`,
	assignAgentToUserByAgentType: ({ userId, agentType, onlyIfNotAssigned }: IAssignAgentToUserParams) =>
		`/agent-assignment/user/${userId}?agentType=${agentType}&onlyIfNotAssigned=${onlyIfNotAssigned}`,
	activePlan: (userId: string) => `/chronic-care/onboarding/active?userId=${userId}`,
	getOnboardingStatus: (userId: string) => `/chronic-care/onboarding/status?userId=${userId}`,
	getBundleDetails: `/bundle`,
	getAudioRecordingLink: (appointmentId: number) => `/orders/consultation/${appointmentId}/recording/signed-url?fileName=final.mp3&isPartialRecording=false&method=GET`,
	searchDoctorsWithAvailabilityV3: "/doctor/search-with-availability/v3",
	getConsultRecap: "/consult/pre-cap/search",
	fetchGlpMedicineOrderSummary: "/kick-starter/glp-medicine/order-summary",
	glpApprovalAction: "/orders/consultation/approval/action",
	glpApprovalQueue: "/orders/consultation/approval/queue",
	glpApprovalHistory: "/orders/consultation/approval/history",
}
export default class AlbusApis implements IAlbusApis {
	getServiceUrl = (url: string) => ALBUS_API_PREFIX + url
	// static addSymptomForReview(name: any) {
	// 	return ApiUtils.apiPostRequest(AlbusUrlConfigs.getAddSymptomForReviewUrl(), JSON.stringify({ name }));
	// }
	//
	// getPatientMedicalProfile(patientId: number): Promise<IEHRSavePatientMedicalProfile> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetPatientMedicalProfile(patientId);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	getUserDoctorHistory(userId: number): Promise<IDocCoachHistory> {
		const currUrl: string = this.getServiceUrl(AlbusApiUrls.userDoctorHistory(userId.toString()))
		return ApiUtils.get(currUrl)
	}
	//
	// getPhysicalAssessmentProfile(patientId: number, appointmentId: number, patientMedicalProfile: any): Promise<any> {
	// 	throw new Error("Method not implemented.");
	// }
	//
	// submitConsultationFeedback(feedbackData: ConsultationFeedbackPayload): Promise<any> {
	// 	return ApiUtils.apiPostRequest(
	// 		AlbusUrlConfigs.getConsultationFeedbackUrl(),
	// 		JSON.stringify({
	// 			feedbackForm: feedbackData,
	// 		}),
	// 		{
	// 			"Content-Type": "application/json",
	// 		}
	// 	);
	// }
	//
	// createGetLabTestsUrl(input: string): Promise<any> {
	// 	throw new Error("Method not implemented.");
	// }
	//
	// savePatientDiagnosticLabTests(metric: import("@curefit/metrics-common").IAddMetric): Promise<any> {
	// 	throw new Error("Method not implemented.");
	// }
	//
	// getAccountData(): Promise<IAccountData> {
	// 	return null;
	// }
	//
	getPatientCompleteDetailsById(patientId: number): Promise<any> {
		const url: string = this.getServiceUrl(AlbusApiUrls.patientDetails + "?" + ApiUtils.requestToQueryParams({ patientId, startEpoch: Date.now(), doctorType: "GP" }))
		return ApiUtils.get(url)
	}
	//
	// getPatientProfileById(patientId: number): Promise<IPatientDetail[]> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetPatientByIdUrl(patientId);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	//
	// saveDoctor(doctorProfile: DoctorProfile): Promise<DoctorProfile> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetDoctorProfileByIdUrl(doctorProfile.id);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// getSurgeryAndProcedureHistoryData(patientId: number): Promise<IPatientSurgeryAndProcedure[]> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetSurgeryAndProcedureHistoryURL(patientId);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// saveSurgeryAndProcedureHistoryData(patientId: number, surgeryToSave: IPatientSurgeryAndProcedure[]): Promise<IPatientSurgeryAndProcedure[]> {
	// 	let currUrl: string = AlbusUrlConfigs.createSaveSurgeryAndProcedureHistoryURL(patientId);
	// 	return ApiUtils.apiPostRequest(currUrl, JSON.stringify(surgeryToSave));
	// }
	//
	// getAllergyData(patientId: number): Promise<IPatientAllergy[]> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetAllergyHistoryURL(patientId);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// saveAllergyData(patientId: number, allergyToSave: IPatientAllergy[]): Promise<IPatientAllergy[]> {
	// 	let currUrl: string = AlbusUrlConfigs.createSaveAllergyHistoryURL(patientId);
	// 	return ApiUtils.apiPostRequest(currUrl, JSON.stringify(allergyToSave));
	// }
	//
	// getHabitData(patientId: number): Promise<IHabit[]> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetHabitHistoryURL(patientId);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// saveHabitData(patientId: number, habitToSave: IHabit[]): Promise<IHabit[]> {
	// 	let currUrl: string = AlbusUrlConfigs.createSaveHabitHistoryURL(patientId);
	// 	return ApiUtils.apiPostRequest(currUrl, JSON.stringify(habitToSave));
	// }
	//
	// getCurrentMedicationData(patientId: number): Promise<ICurrentMedication[]> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetCurrentMedicationURL(patientId);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// saveCurrentMedicationData(patientId: number, medsToSave: IMedicinePrescription[]): Promise<IMedicinePrescription[]> {
	// 	let currUrl: string = AlbusUrlConfigs.createSaveCurrentMedicationURL(patientId);
	// 	return ApiUtils.apiPostRequest(currUrl, JSON.stringify(medsToSave));
	// }
	//
	// getComorbidConditionData(patientId: number): Promise<IPatientCondition[]> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetComorbidConditionsURL(patientId);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// saveComorbidConditionData(patientId: number, conditions: IPatientCondition[]): Promise<IPatientCondition[]> {
	// 	let currUrl: string = AlbusUrlConfigs.createSaveComorbidConditionsURL(patientId);
	// 	return ApiUtils.apiPostRequest(currUrl, JSON.stringify(conditions));
	// }
	//
	// getGeneralExamination(): Promise<IGeneralExamination[]> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetGeneralExaminationUrl();
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// getVitals(): Promise<Vital[]> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetVitalsUrl();
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	saveVitals(patientId: number, patientVital: IVitalSaveRequest[]): Promise<any> {
		const currUrl: string = this.getServiceUrl(AlbusApiUrls.savePatientVitals(patientId))
		return ApiUtils.post(currUrl, patientVital)
	}

	// getPatientVitals(patientId: number): Promise<IPatientVitalResponse[]> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetAllVitalForPatientUrl(patientId);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// getPatientLabTests(patientId: number, requestParams: IDiagnosticOrderRequestParams): Promise<ILabTestMaster[]> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetAllLabTestsForPatientUrl(patientId, requestParams);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	getAppointments(requestParams: IAppointmentRequestParams): Promise<IAppointment[]> {
		const url: string = this.getServiceUrl(AlbusApiUrls.appointment + "?" + ApiUtils.requestToQueryParams(requestParams))
		return ApiUtils.get(url, null, null, null, null, 10000)
	}

	getPaginatedAppointments(requestParams: IAppointmentRequestParams): Promise<PaginatedResponse<IAppointment>> {
		const url: string = this.getServiceUrl(AlbusApiUrls.paginatedAppointment + "?" + ApiUtils.requestToQueryParams(requestParams))
		return ApiUtils.get(url)
	}
	//
	// getAppointmentById(appointmentId: number): Promise<IAppointmentScheduleResponse> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetAppointmentDetailsById(appointmentId);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	getTwilioContext(requestParams: ICommAccessTokenRequestParams): Promise<IAccessToken[]> {
		const currUrl: string = this.getServiceUrl(`${AlbusApiUrls.getTwilioAccessTokenList}?${ApiUtils.requestToQueryParams(requestParams)}`)
		return ApiUtils.get(currUrl)
	}
	//
	// getVideoContext(appointmentId: number): Promise<any> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetVideoContextUrl(appointmentId);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// getStaticData(staticDataRequestParams: IStaticDataRequestParams): Promise<IStaticDataResponse> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetStaticDataUrl(staticDataRequestParams);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// savePatientPrescription(appointmentId: number, prescription: IPrescription): Promise<IPrescription> {
	// 	let currUrl: string = AlbusUrlConfigs.createPatientPrescriptionUrl(appointmentId);
	// 	return ApiUtils.apiPostRequest(currUrl, JSON.stringify(prescription));
	// }
	//
	// saveDifferentialDiagnosis(appointmentId: number, differentialDiagnosis: IDifferentialDiagnosis): Promise<any> {
	// 	let currUrl: string = AlbusUrlConfigs.createSaveDifferentialDiagnosisUrl(appointmentId);
	// 	return ApiUtils.apiPostRequest(currUrl, JSON.stringify(differentialDiagnosis));
	// }
	//
	// savePrivateNotes(appointmentId: number, privateNotes: IPrivateNotes) {
	// 	let currUrl: string = AlbusUrlConfigs.createSavePrivateNotesUrl(appointmentId);
	// 	return ApiUtils.apiPostRequest(currUrl, JSON.stringify(privateNotes));
	// }
	//
	// fetchCaseSummary(appointmentId: number) {
	// 	let currUrl: string = AlbusUrlConfigs.fetchCaseSummary(appointmentId);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	updateAppointmentStatus(bookingId: number, appointmentId: number, requestParams: IAppointmentStatusRequestParam): Promise<any> {
		const currUrl: string = this.getServiceUrl(AlbusApiUrls.updateStatusAppointment(bookingId, appointmentId))
		return ApiUtils.post(currUrl, requestParams, true, "", UpdateAppointmentStatusTimeoutInSeconds * 1000, true, 10000)
	}
	//
	// updateAppointment(bookingId: number, appointmentId: number, requestParams: IConsultationUpdateParams): Promise<IAppointmentScheduleResponse> {
	// 	let currUrl: string = AlbusUrlConfigs.createUpdateAppointmentStatusUrl(bookingId, appointmentId);
	// 	return ApiUtils.apiPostRequest(currUrl, JSON.stringify(requestParams));
	// }
	//
	// getIsActiveCovidPack(userId: number) {
	// 	return ApiUtils.apiGetRequest(AlbusUrlConfigs.getIsActiveCovidPack(userId));
	// }
	//
	getChronicCareSubscription(subscriptionStatus: ChronicCareSubscriptionStatus, userId: string) {
		return ApiUtils.get(this.getServiceUrl(AlbusApiUrls.chronicCareSubscription[subscriptionStatus](userId)))
	}
	//
	getUserPreference(userId: string, preferenceType: "META" | "OVERRIDE", preferenceTypeAttribute: PreferenceTypeAttributeEnum) {
		return ApiUtils.get(this.getServiceUrl(AlbusApiUrls.userPreference(userId, preferenceType, preferenceTypeAttribute)))
	}

	updateUserPreference(params: IUpdateUserPreferenceParams) {
		const url = this.getServiceUrl(AlbusApiUrls.updateUserPreference)
		return ApiUtils.post(url, params)
	}

	getPreferredDoctor(userId: string, patientId: string) {
		return ApiUtils.get(this.getServiceUrl(AlbusApiUrls.preferredDoctor(userId, patientId, getPatientTenant())))
	}
	//
	// getCovidMetrics() {
	// 	return ApiUtils.apiPostRequest(AlbusUrlConfigs.getCovidMetricIds(), "{}");
	// }f
	//
	// getCovidVitals(startEpoch: number, endEpoch: number, metricIds: string[], patientId: number, userId: string) {
	// 	return ApiUtils.apiGetRequest(AlbusUrlConfigs.getCovidVitals(startEpoch, endEpoch, metricIds, patientId, userId));
	// }
	//
	// /* TYPE AHEAD APIS START */
	// getMedicineByName(medicineName: string): Promise<IMedicineSuggestions[]> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetMedicinesUrl(medicineName);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// getMedicineByMolecule(genericName: string): Promise<IMedicineSuggestions[]> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetMedicineByMoleculesUrl(genericName);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// getSymptomsByName(symptomName: string): Promise<any> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetSymptomsByNameUrl(symptomName);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// getDiagnosticSuggestions(input: string): Promise<IDiagnosticSuggestions[]> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetDiagnosticsUrl(input);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// getDiseaseByName(disease: string): Promise<any> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetDiseasesUrl(disease);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// getLifeStyleByName(lifestyle: string): Promise<any> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetLifeStyleUrl(lifestyle);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// getAllergiesByName(allergy: string): Promise<any> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetAllergiesUrl(allergy);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// getFitnessActivities(activity: string): Promise<any> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetFitnessActivitiesUrl(activity);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// /* TYPE AHEAD APIS END */
	//
	// getCenterDetails(centerId: number): Promise<ICenter> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetCenterDetailsUrl(centerId);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// getResources(resourceRequestParams: IResourceRequestParams) {
	// 	let currUrl: string = AlbusUrlConfigs.createGetResourcesUrl(resourceRequestParams);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// getPatientPastSession(patientId: number, subSectionKey: string) {
	// 	let currUrl: string = AlbusUrlConfigs.getPatientPastSessionsURL(patientId, subSectionKey);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// uploadPrescription(id: number, file: File, type: string): Promise<any> {
	// 	let currUrl: string = AlbusUrlConfigs.createUploadPrescriptionUrl(id, type);
	// 	return ApiUtils.apiFormDataRequest(currUrl, file);
	// }
	//
	// uploadPlan(id: number, file: File, type: string): Promise<any> {
	// 	let currUrl: string = AlbusUrlConfigs.createUploadPlanUrl(id, type);
	// 	return ApiUtils.apiFormDataRequest(currUrl, file);
	// }
	//
	emailPrescription(bookingId: number, appointmentId: number): Promise<any> {
		return ApiUtils.get(this.getServiceUrl(AlbusApiUrls.sendPrescription(bookingId, appointmentId)))
	}
	//
	// emailPlan(appointmentId: number, curefitUserId: string, bookingId: number): Promise<any> {
	// 	let currUrl: string = AlbusUrlConfigs.createSendPlanUrl(appointmentId, curefitUserId, bookingId);
	// 	return ApiUtils.apiGetRequest(currUrl);
	// }
	//
	// updatePatientPersonalInfo(id: string, personalInfo: PatientPersonalProfile): Promise<PatientPersonalProfile> {
	// 	let currUrl: string = AlbusUrlConfigs.createPatientPersonalUrl(id);
	// 	console.log("UPDATE_PATIENT_PROILE< START", currUrl);
	// 	return ApiUtils.apiPostRequest(currUrl, JSON.stringify(personalInfo));
	// }
	//
	// fetchPatientPersonalInfo(id: string): Promise<PatientPersonalProfile> {
	// 	let currUrl: string = AlbusUrlConfigs.createPatientPersonalUrl(id);
	// 	return new Promise((resolve: any, reject: any) => {
	// 		ApiUtils.apiGetRequest(currUrl, true, AppConstants.FETCH_PATIENT_PERSONAL_PROFILE_TIMEOUT_IN_SECONDS)
	// 			.then((jsonData: any) => {
	// 				let resource: PatientPersonalProfile = TypedJSON.parse(jsonData);
	// 				resolve(Object.assign({}, resource));
	// 			})
	// 			.catch((error) => {
	// 				reject(error);
	// 			});
	// 	});
	// }
	//
	// fetchStaticRoutineAttr(): Promise<RoutineAttribute> {
	// 	let currUrl: string = AlbusUrlConfigs.createStaticRoutineAttrURL();
	// 	return new Promise((resolve: any, reject: any) => {
	// 		ApiUtils.apiGetRequest(currUrl)
	// 			.then((jsonData: any) => {
	// 				let attributes: RoutineAttribute = TypedJSON.parse(jsonData, RoutineAttribute);
	// 				resolve(Object.assign({}, attributes));
	// 			})
	// 			.catch((error) => {
	// 				reject(error);
	// 			});
	// 	});
	// }
	//
	logOrderEvent(orderEvent: IOrderEvent): Promise<IOrderEvent> {
		return ApiUtils.post(this.getServiceUrl(AlbusApiUrls.logOrderEvent), orderEvent)
	}
	//
	// fetchPatientTypeFormData(patientId: number): Promise<ITypeFormResponse> {
	// 	let currUrl: string = AlbusUrlConfigs.createGetTypeFormDataUrl(patientId);
	// 	return ApiUtils.apiGetRequest(currUrl, true);
	// }
	//
	// fetchUnreadMessageInfo(doctorId: string): Promise<IUnreadMsgInfo[]> {
	// 	let reqParams: IUnreadMsgInfoRequestParams = {
	// 		fromTime: AppUtils.getFetchUnreadMessageFromTime(),
	// 		toTime: AppUtils.getFetchUnreadMessageToTime(),
	// 	};
	// 	let currUrl: string = AlbusUrlConfigs.createFetchUnreadMessageInfoDataUrl(doctorId, reqParams);
	// 	return ApiUtils.apiGetRequest(currUrl, true);
	// }
	//
	// updateConsumptionHorizon(channelSid: string, memberUniqueIdentity: string, lastConsumedMessageIndex: number, lastMessageConsumedTimestamp: number): Promise<any> {
	// 	let req = {
	// 		channelSid,
	// 		memberUniqueIdentity,
	// 		lastConsumedMessageIndex,
	// 		lastMessageConsumedTimestamp,
	// 	};
	// 	return ApiUtils.apiPostRequest(AlbusUrlConfigs.createUpdateConsumptionHorizonUrl(), JSON.stringify(req));
	// }
	//
	addTwilioRoomEventLog(reqBody: IRoomEventLog[], headers?: any): Promise<any> {
		return ApiUtils.post(this.getServiceUrl(AlbusApiUrls.logRoomEvent), reqBody, headers)
	}
	//
	// fetchConsultationProductName(categoryCode: string, productCode: string): Promise<any> {
	// 	return ApiUtils.apiGetRequest(AlbusUrlConfigs.createFetchConsultationProductUrl(categoryCode, productCode));
	// }
	//
	searchPatients(curefitUserId: string, relationship?: string): Promise<any> {
		return ApiUtils.get(this.getServiceUrl(AlbusApiUrls.searchPatient(curefitUserId, relationship)))
	}
	getDoctorSignatureImageDownloadPreSignedUrl(doctorId: number): Promise<IDoctorImageSignedUrl> {
		return ApiUtils.get(this.getServiceUrl(AlbusApiUrls.doctorSignedSignatureImage(doctorId)))
	}
	//
	// searchDoctorPatients(doctorId: any, query: any) {
	// 	return ApiUtils.apiGetRequest(AlbusUrlConfigs.getSearchDoctorPatientsUrl(doctorId, query));
	// }
	//
	// addSymptomForReview(name: string): Promise<any> {
	// 	return ApiUtils.apiPostRequest(AlbusUrlConfigs.getAddSymptomForReviewUrl(), JSON.stringify({ name }));
	// }
	//
	// fetchAppointmentChatContextAndOrderAssetsById(appointmentId: number, isPackInfoRequired?: boolean): Promise<IAppointmentScheduleResponse> {
	// 	let reqBuilder: AppointmentScheduleRequestParamsBuilder = new AppointmentScheduleRequestParamsBuilder();
	// 	reqBuilder
	// 		.setAppointmentId(appointmentId)
	// 		.setAppointmentContextRequired()
	// 		.setDoctorRequired()
	// 		.setConsultationProductRequired()
	// 		.setPatientRequired()
	// 		.setIsAvailableDoctorActionRequired()
	// 		.setOrderAssetsRequired()
	// 		.setPrescriptionEditContextRequired()
	// 		.setPackInfoRequired(isPackInfoRequired)
	// 		.setIsOrderEventLoggingRequired();
	// 	return new Promise((resolve, reject) => {
	// 		this.getAppointments(reqBuilder.build())
	// 			.then((res: IAppointmentScheduleResponse[]) => {
	// 				resolve(res[0]);
	// 			})
	// 			.catch((err: any) => {
	// 				reject(err);
	// 			});
	// 	});
	// }
	//
	fetchRoster(params: IRosterRequest): Promise<IResourceSchedule[]> {
		const url: string = this.getServiceUrl(AlbusApiUrls.getSchedule + `?${ApiUtils.requestToQueryParams(params)}`)
		return ApiUtils.get(url)
	}

	updateRoster(params: IAddRosterRequest[], action: ROSTER_UPDATE_ACTIONS = ROSTER_UPDATE_ACTIONS.CONFIRM): Promise<IResourceScheduleResponse> {
		const url: string = this.getServiceUrl(AlbusApiUrls.updateRoster(action))
		return ApiUtils.put(url, params)
	}

	getConsultationsCount(doctorId: string | number, fromTimeEpoch: number): Promise<IConsultationCountResponse> {
		const url = this.getServiceUrl(`${AlbusApiUrls.consultationCount}?${ApiUtils.requestToQueryParams({ doctorId, fromTimeEpoch })}`)
		return ApiUtils.get(url)
	}
	//
	// getDocPreSignedUrlForUpload(referenceType: string, referenceId: number): Promise<any> {
	// 	return ApiUtils.apiGetRequest(AlbusUrlConfigs.getPreSignedUrlForUpload(referenceType, referenceId.toString()));
	// }
	//
	// getDocPreSignedUrlForDownload(referenceType: string, referenceId: string, s3KeyName: string): Promise<any> {
	// 	return ApiUtils.apiGetRequest(AlbusUrlConfigs.getDocPreSignedUrlForDownload(referenceType, referenceId, s3KeyName));
	// }
	//
	// fetchAccessPolicyForCurrentUser(
	// 	principleReferenceEntity: string,
	// 	ownerReferenceEntity: string,
	// 	ownerReferenceEntityId: string,
	// 	resourceType: string,
	// 	principleReferenceEntityId: string
	// ): Promise<any> {
	// 	let url: string = AlbusUrlConfigs.getAccessPolicyFetchUrl(principleReferenceEntity, ownerReferenceEntity, ownerReferenceEntityId, resourceType, principleReferenceEntityId);
	// 	// this api returns 204 response with
	// 	return fetch(url).then((res) => res.text());
	// }
	//
	// setAccessPolicyForCurrentUser(params: ConsentParam): Promise<any> {
	// 	let url: string = AlbusUrlConfigs.getAccessPolicyPutUrl();
	// 	return ApiUtils.apiPutRequest(url, JSON.stringify(params));
	// }
	//
	// fetchRecomTemplates = (doctorId: number, count: number): Promise<IRecomTemplate> => {
	// 	const url = AlbusUrlConfigs.getRecoTreatementPlanFetchUrl(doctorId, count);
	// 	return ApiUtils.apiGetRequest(url, true);
	// };
	//
	// deleteAgentRoster = (jsonBody: IDeleteAgentRoster): Promise<ResourceScheduleResponse> => {
	// 	return ApiUtils.apiPostRequest(AlbusUrlConfigs.getDeleteAgentRosterUrl(), JSON.stringify(jsonBody));
	// };
	//
	// fetchGlucoseLevels(levelCode?: GlucoseLevelCode): Promise<IGlucoseLevels[]> {
	// 	return ApiUtils.apiGetRequest(AlbusUrlConfigs.getFetchGlucoseLevelsUrl(levelCode));
	// }
	//
	// fetchCgmReport(userId: string): Promise<ICgmReport> {
	// 	return ApiUtils.apiGetRequest(AlbusUrlConfigs.getFetchCgmReportUrl(userId));
	// }
	//
	// fetchCgmStats(userId: string, deviceId: string, mostRecentStat: boolean = true): Promise<ICgmStats> {
	// 	return ApiUtils.apiGetRequest(AlbusUrlConfigs.getFetchCgmStatsUrl(userId, deviceId, mostRecentStat));
	// }
	//
	// fetchWhatsAppUnreadMessageInfo(doctorId: string): Promise<IWhatsAppUnreadMsgInfoWrapper[]> {
	// 	let currUrl: string = AlbusUrlConfigs.createFetchWhatsAppUnreadMessageInfoDataUrl(doctorId);
	// 	return ApiUtils.apiGetRequest(currUrl, true);
	// }
	//
	getPatientActivePackDetails(userId: string) {
		return ApiUtils.get(this.getServiceUrl(AlbusApiUrls.activePlan(userId)))
	}
	//
	// fetchCgmActivities(searchParams: ICgmActivitiesSearchParams) {
	// 	const url = AlbusUrlConfigs.getCgmActivitiesUrl();
	// 	return ApiUtils.apiPostRequest(url, JSON.stringify(searchParams));
	// }
	//
	// getUserInfo(userId: string) {
	// 	return ApiUtils.apiGetRequest(AlbusUrlConfigs.getUserInfoUrl(userId));
	// }
	//
	// getBulkUserInfo(userIds: number[]) {
	// 	const encodedParams = window.encodeURIComponent(userIds.toString());
	// 	return ApiUtils.apiGetRequest(AlbusUrlConfigs.getBulkUserInfoUrl(encodedParams));
	// }
	//
	// getFitnessDeviceSections() {
	// 	return ApiUtils.apiGetRequest(AlbusUrlConfigs.getFitnessDeviceSectionUrl());
	// }
	//
	// fetchFitnessDeviceData(searchParams: IFitnessDeviceDataSearchParam) {
	// 	const url = AlbusUrlConfigs.getFitnessDeviceDataUrl();
	// 	return ApiUtils.apiPostRequest(url, JSON.stringify(searchParams));
	// }
	//
	fetchDocumentUpload(patientId: number): Promise<IDocument[]> {
		return ApiUtils.get(this.getServiceUrl(AlbusApiUrls.patientDocuments(patientId)))
	}
	//
	// fetchDocumentPdf(s3Prefix: string) {
	// 	const url = AlbusUrlConfigs.createDocumentPdfURL(encodeURIComponent(s3Prefix));
	// 	return ApiUtils.apiPostRequest(url, JSON.stringify({ s3Prefix }));
	// }
	//
	// uploadPatientDocs(req: Record<string, any>) {
	// 	return ApiUtils.apiFormDataPostRequest(UPLOAD_PATIENT_DOC, req);
	// }
	//
	// uploadPatientDocsV2(req: Record<string, any>) {
	// 	return ApiUtils.apiPostRequest(UPLOAD_PATIENT_DOC_V2, JSON.stringify(req));
	// }
	//
	fetchConsultationSlotsForDoctor(payload: ConsultationSlotsRequest): Promise<ConsultationSlotsResponse> {
		if (isNil(payload.bookingMode)) payload.bookingMode = "CMT_USER"
		if (isNil(payload.daysToGo)) payload.daysToGo = 2
		const url = this.getServiceUrl(AlbusApiUrls.fetchConsultationSlotsForDoctor)
		return ApiUtils.post(url, payload)
	}

	// deletePatientDoc(documentId: string) {
	// 	const url = AlbusUrlConfigs.createDeletePatientDocURL(documentId);
	// 	return ApiUtils.apiDeleteRequest(url, JSON.stringify(null));
	// }
	uploadPatientDocsV2(req: Record<string, any>) {
		const url = this.getServiceUrl(AlbusApiUrls.uploadPatientDocV2)
		return ApiUtils.post(url, req)
	}

	approvePatientDocument(documentId: number, approved: boolean, remarks?: string): Promise<IDocument> {
		const url = this.getServiceUrl(AlbusApiUrls.approvePatientDocument(documentId, approved, remarks))
		return ApiUtils.patch(url, null)
	}

	//
	deletePatientDoc(documentId: string) {
		const url = this.getServiceUrl(AlbusApiUrls.deleteDocument(documentId))
		return ApiUtils.delete(url, null)
	}
	//
	requestAudioRecording(appointmentId: number): Promise<any> {
		return ApiUtils.get(this.getServiceUrl(AlbusApiUrls.requestAudioRecording(appointmentId)))
	}

	fetchDiagnosticBookingSlots(payload: IFetchDiagnosticsBookingSlotsPayload): Promise<IFetchDiagnosticsBookingSlotsResponse> {
		const url = this.getServiceUrl(AlbusApiUrls.fetchDiagnosticSlotsAtHome)
		return ApiUtils.post(url, payload)
	}

	fetchConciseDiagnosticeOrders(payload: IFetchConciseDiagnosticOrdersPayload): Promise<IFetchConciseDiagnosticOrdersResponse> {
		const url = this.getServiceUrl(AlbusApiUrls.fetchConciseDiagnosticOrders(payload.patientId))
		return ApiUtils.get(url)
	}

	fetchUserContactNumbers(userId: number, addCountryPrefixToNumber = false): Promise<IUserContactNumbers> {
		return ApiUtils.get(this.getServiceUrl(AlbusApiUrls.fetchUserContactNumber(addCountryPrefixToNumber, userId)))
	}

	fetchDiagnosticProviderPreference(userId: string): Promise<IFetchDiagnosticProviderPreferenceResponse> {
		const url = this.getServiceUrl(AlbusApiUrls.fetchDiagnosticProviderPreference(userId))
		return ApiUtils.get(url)
	}

	// fetchSecureAssetUploadUrl(params: IFetchSecureAssetUploadUrlParams): Promise<IFetchSecureAssetUploadUrlResponse> {
	// 	const url = AlbusUrlConfigs.fetchSecureAssetUploadUrl(params);
	// 	return ApiUtils.apiGetRequest(url);
	// }
	//
	//
	// fetchDiagnosticProviderPreference(userId: string): Promise<IFetchDiagnosticProviderPreferenceResponse> {
	// 	const url = AlbusUrlConfigs.fetchDiagnosticProviderPreferenceUrl(userId);
	// 	return ApiUtils.apiGetRequest(url);
	// }

	fetchSecureAssetUploadUrl(params: IFetchSecureAssetUploadUrlParams): Promise<IFetchSecureAssetUploadUrlResponse> {
		const url = this.getServiceUrl(AlbusApiUrls.fetchSecureAssetUrl) + `?${ApiUtils.requestToQueryParams(params)}`
		return ApiUtils.get(url)
	}

	fetchVideoConsulationData(patientId: number, startDate: number, endDate: number): Promise<IAppointment[]> {
		const reqBuilder: AppointmentScheduleRequestParamsBuilder = new AppointmentScheduleRequestParamsBuilder()
		reqBuilder.setPatientId(patientId)
		reqBuilder.setStartTimeEpoch(startDate)
		reqBuilder.setTillTimeEpoch(endDate)
		return new Promise((resolve, reject) => {
			this.getAppointments(reqBuilder.build())
				.then((videoConsults) => {
					resolve(videoConsults)
				})
				.catch((error) => {
					reject(error)
				})
		})
	}
	triggerVideoCallNotification(roomId: string): Promise<any> {
		const url = this.getServiceUrl(AlbusApiUrls.triggerVideoCallNotification(roomId))
		return ApiUtils.post(url, {})
	}

	fetchBundleDetails(param: Partial<any>): Promise<any> {
		const url = this.getServiceUrl(AlbusApiUrls.getBundleDetails) + "?" + ApiUtils.requestToQueryParams(param)
		return ApiUtils.get(url)
	}

	fetchSelfInstallationBookingStatus(userId: string): Promise<IFetchSelfInstallationBookingStatusResponse> {
		const url = this.getServiceUrl(AlbusApiUrls.fetchSelfInstallationBookingStatus(userId))
		return ApiUtils.get(url)
	}

	fetchRosterLeaves = (params: IFetchRosterLeavesParams): Promise<IFetchRosterLeavesResponse> => {
		const url = this.getServiceUrl(AlbusApiUrls.fetchRosterLeaves(params))
		return ApiUtils.get(url)
	}

	assignAgentToUser = (params: IAssignAgentToUserParams): Promise<IAssignAgentToUserResponse> => {
		const url = this.getServiceUrl(AlbusApiUrls.assignAgentToUserByAgentType(params))
		return ApiUtils.post(url, null)
	}

	getUserOnboardingStatus = (userId: string): Promise<IGetOnboardingStatusResponse> => {
		const url = this.getServiceUrl(AlbusApiUrls.getOnboardingStatus(userId))
		return ApiUtils.get(url)
	}

	getAudioRecordingUrl = (appointmentId: number): Promise<{ signedUrl: string }> => {
		const url = this.getServiceUrl(AlbusApiUrls.getAudioRecordingLink(appointmentId))
		return ApiUtils.get(url)
	}
	
	searchDoctorsWithAvailabilityV3 = (params: ISearchDoctorsWithAvailabilityV3Payload): Promise<ISearchDoctorsWithAvailabilityV3Response[]> => {
		const url = this.getServiceUrl(AlbusApiUrls.searchDoctorsWithAvailabilityV3)
		return ApiUtils.post(url, params)
	}

	getConsultRecap = (payload: IBaseGetSearchPayload): Promise<IBaseGetSearchResponse<IGetConsultRecapResponse>> => {
		const query = payload.query;
		delete payload.query;
		const url = this.getServiceUrl(AlbusApiUrls.getConsultRecap) + `?${ApiUtils.requestToQueryParams(payload)}&query=${query}`
		return ApiUtils.get(url)
	}

	fetchGlpMedicineOrderSummary = (payload: IFetchGlpMedicineOrderSummaryPayload): Promise<IUserProductOrderSummary> => {
		const url = this.getServiceUrl(AlbusApiUrls.fetchGlpMedicineOrderSummary)
		return ApiUtils.post(url, payload)
	}

	submitGlpApprovalAction = (payload: IGlpApprovalActionRequest): Promise<IPrescriptionApproval> => {
		const url = this.getServiceUrl(AlbusApiUrls.glpApprovalAction) + `?doctorId=${payload.imDoctorId ?? ""}`
		return ApiUtils.post(url, payload)
	}

	getGlpApprovalQueue = (view: "pending" | "my-require-consult" = "pending", doctorId?: number): Promise<any> => {
		const url = this.getServiceUrl(AlbusApiUrls.glpApprovalQueue) + `?view=${encodeURIComponent(view)}&doctorId=${doctorId ?? ""}`
		return ApiUtils.get(url)
	}

	getGlpApprovalHistory = (appointmentId: number, doctorId: number): Promise<IPrescriptionApproval[]> => {
		const url = this.getServiceUrl(AlbusApiUrls.glpApprovalHistory) + `?appointmentId=${appointmentId}&doctorId=${doctorId}`
		return ApiUtils.get(url)
	}
}
